import { Component, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren, ElementRef } from '@angular/core';
import { ModalService, ToastService, UploadFileState } from '@siemens/ix-angular';
import { ApiService } from 'src/app/services/api.service';
import { StandardPhonebook } from 'src/app/utils/standard-phonebook';
import { CrmtFileFactory } from 'src/app/utils/crmt-file-parser';
import { CrmtFile, ICrmtFile } from 'src/app/utils/crmt-file';
import { FileListItem } from 'src/app/components/cloud-file-selector/cloud-file-selector.component';
import { FileListComponent } from 'src/app/components/file-list/file-list.component';
import { PermissionsService, UserLevel } from 'src/app/services/permissions.service';
import { EditStateService } from 'src/app/services/edit-state.service';
import { FileHeaderService } from 'src/app/services/file-header.service';
import { switchMap } from 'rxjs';
import { DuplicateValueCheckServiceService } from 'src/app/services/duplicate-value-check-service.service';

type DeleteFileBody = {
  fileName: string,
  comment?: string,
}

type LocalConfig =  {

  type: string
  fileCount: number;
  fileTypes: string[];
}

@Component({
  selector: 'app-file-edit-page',
  templateUrl: './file-edit-page.component.html',
  styleUrls: ['./file-edit-page.component.css']
})
export class FileEditPageComponent implements OnInit {

  @ViewChild('customToast', { read: TemplateRef })
  customModalRefToast!: TemplateRef<any>;

  @ViewChild('customModal', { read: TemplateRef })
  customModalRef!: TemplateRef<any>;

  @ViewChild('customModalForDeleteWindow', { read: TemplateRef })
  customModalRefForDeleteWindow!: TemplateRef<any>;

  @ViewChild('customModal', { read: FileListComponent })
  customModalRef2!: FileListComponent

  @ViewChild('customModalForUnsavedChanges', { read: TemplateRef })
  customModalForUnsavedChangesRef!: TemplateRef<any>;

  @ViewChild(FileListComponent) fileListComponent?: FileListComponent

  @ViewChildren(FileListComponent) public helloComponents = new QueryList<FileListComponent>();

  public errorMessage: string = "";
  @ViewChild('customModalForErrorMessage', { read: TemplateRef })
  customModalForErrorMessage!: TemplateRef<any>;

  public state = UploadFileState.SELECT_FILE;
  public files: File[] = []
  public localOperationsDisabled = true
  public cloudOperationsDisabled = true
  public saveAndUploadDisabled = true
  public newButtonDisable = false
  public loadButtonDisable = false
  public file: CrmtFile = new StandardPhonebook({ name: 'un-named', majorVersion: 0, minorVersion: 0 })
  public fileList: FileListItem[] = []
  public disableStandard = true
  public rowSelection: "single" | "multiple" = "single";
  public allowedFileSize:number = 1048576;

  public expectedFileConfig: LocalConfig = this.getConfig("");
  public validExtensions = "";
  private fileType: string = 'standard-phonebook'
  private deviceType?: string
  private fileForDownload: string | undefined = undefined
  private filesForDelete: string[] = []
  public isLoading: boolean = false
  public enableEditOption: boolean = false
  public isError: boolean = false
  private originalSelectedValue?: string
  public downloadOkOptionDisabled: boolean = true
  public deleteOptionDisabled: boolean = true
  public fileTypeSelected = false;

  constructor(
    private readonly permissions: PermissionsService,
    private _apiService: ApiService,
    private readonly toastService: ToastService,
    private readonly modalService: ModalService,
    private readonly editStateService: EditStateService,
    private readonly elementRef: ElementRef,
    private fileHeaderService: FileHeaderService,
    private duplicateValueCheckService: DuplicateValueCheckServiceService
  ) { }

  ngOnInit(): void {

    this.permissions.getUserLevel().subscribe(level => {

      this.disableStandard = level < UserLevel.STANDARD
    })
  }

  validatefileNameVersion(): boolean {
    const fileName = this.fileHeaderService.getFileHeaderValues().name
    const majorVersion = this.fileHeaderService.getFileHeaderValues().majorVersion
    const versionStr = this.fileHeaderService.getFileHeaderValues().versionStr

    if (fileName === '' && (Number.isNaN(majorVersion))) {
      this.errorMessage = 'File name and version can not be empty.'
      this.openModalForErrorMessage()
      return false
    } else if (fileName === '') {
      this.errorMessage = 'File name can not be empty.'
      this.openModalForErrorMessage()
      return false
    } else if (Number.isNaN(majorVersion)) {
      if (versionStr === '') {
        this.errorMessage = 'File version can not be empty.'
        this.openModalForErrorMessage()
      } else {
        this.errorMessage = 'File version is not valid. Enter numbers only'
        this.openModalForErrorMessage()
      }
      return false
    } else {
      return true
    }
  }

  filesChanged(event: any) {
    const fileSize = event.detail[0].size;
    const files = event.detail
    const filename = files[0].name

    if(this.validateFileSelection(filename, fileSize, this.expectedFileConfig)){
      let fileReader: FileReader = new FileReader();

      fileReader.onloadend = (e) => {

        try {
          this.file = CrmtFileFactory.parse(
            this.fileType,
            fileReader.result as ArrayBuffer);
          this.enableEditOption = true
          this.saveAndUploadDisabled = false
          this.showLoadSuccessToastMessage();
        }
        catch (e: any) {
          this.errorMessage = `Error reading ${filename}. Make sure you loaded a valid file.`
          this.openModalForErrorMessage()
        }
      }
      fileReader.readAsArrayBuffer(files[0]);
    }
  }

  private implementsICrmtFile(obj: CrmtFile): obj is ICrmtFile {
    return (<ICrmtFile>obj).toRawData !== undefined;
  }

  uploadClick() {
    const iCrmtFile: ICrmtFile = this.file as ICrmtFile;
    const validFileNameVersion = this.validatefileNameVersion();

    if(this.fileType == 'standard-phonebook' || this.fileType == 'extended-phonebook'
      || this.fileType == 'sms-database'){
        if(!this.validateFileData(iCrmtFile)){
          return;
        }
    }

    if (!iCrmtFile || iCrmtFile.entries.length === 0 || !validFileNameVersion) {
      if (validFileNameVersion) {
        this.errorMessage = "Empty file cannot be uploaded.";
        this.openModalForErrorMessage();
      }
      return;
    }

    if (!this.implementsICrmtFile(this.file)) {
      this.errorMessage = 'File type not supported.';
      this.openModalForErrorMessage();
      return;
    }

    if (this.fileType == 'uus-database') {
      // 'true' destinationNumber is required to validate  
      if (!this.validateEntries(this.file, true)) {
        this.errorMessage = "Invalid Data Entry: Destination Number and Message Text in the row are mandatory. Please make the correction to proceed."
        this.openModalForErrorMessage();
        return
      }
    }

    const rawData = this.file.toRawData();
    const extension = this._apiService.getFileExtension(this.fileType);
    const filename = `${this.fileType}/${this.deviceType}/${this.file.header.name}V${this.file.header.majorVersion}.${this.file.header.minorVersion}.${extension}`;

    this.infoToast('Uploading file...');

    this._apiService.uploadFile(filename, rawData).subscribe(() => {
      this.editStateService.setEditedState(false);
      this.successToast('File uploaded.');
    });
  }

  validateFileData(file: any): boolean {
    const uniqueData = this.duplicateValueCheckService.getUniqueData();
    if (!uniqueData && !this.validateEntries(file)) {
      this.errorMessage = "Invalid Data Entry: Duplicate Index Numbers are not permitted. All fields in the row are mandatory. Please make the correction to proceed."
      this.openModalForErrorMessage();
      return false;
    }
    else if (!uniqueData) {
      this.errorMessage = "Invalid Data Entry: Duplicate Index Numbers are not permitted. Please make the correction to proceed."
      this.openModalForErrorMessage();
      return false;
    }
    else if (!this.validateEntries(file)) {
      this.errorMessage = this.fileType === 'sms-database'
        ? "Invalid Data Entry: Index Number and Text Message in the row are mandatory. Please make the correction to proceed."
        : "Invalid Data Entry: All fields in the row are mandatory. Please make the correction to proceed.";
      this.openModalForErrorMessage();
      return false;
    }
    return true
  }

  validateEntries(
    data: { entries: any[] },
    requireDestinationNumber: boolean = false
  ): boolean {
    if (!Array.isArray(data.entries)) return false;

    const requiredKeys = ["number", "description", "textMessage", "messageText"];
    if (requireDestinationNumber) {
      requiredKeys.push("destinationNumber");
    }

    return data.entries.every(entry =>
      requiredKeys.every(key => entry[key] !== null && entry[key] !== "")
    );
  }

  downloadClick() {
    this.rowSelection = "single"
    this.downloadOkOptionDisabled = true
    this.openModal()
  }

  deleteClick() {
    this.rowSelection = "multiple"
    this.deleteOptionDisabled = true
    this.openModalForDeleteWindow()
    this.updateFileList()
  }

  public saveClick() {
    const  iCrmtFile: ICrmtFile =  <ICrmtFile>this.file
    const validfileNameVersion = this.validatefileNameVersion()
    if(this.fileType == 'standard-phonebook' || this.fileType == 'extended-phonebook'
       || this.fileType == 'sms-database'){
        if(!this.validateFileData(iCrmtFile)){
          return;
        }
    } 
    if (iCrmtFile && iCrmtFile.entries.length > 0 && validfileNameVersion) {
      if (this.implementsICrmtFile(this.file)) {
        this.editStateService.setEditedState(false);
        this._apiService.save(this.fileType, this.file)
      }
    } else {
      if (validfileNameVersion) {
        this.errorMessage = "Empty file can not be saved."
        this.openModalForErrorMessage()
      } 
    }
  }

  public newFile() {
    this.enableEditOption = true
    this.saveAndUploadDisabled = false
    this.file = CrmtFileFactory.create(this.fileType)
  }

  async infoToast(message: string) {
    this.toastService.setPosition('top-right');
    this.toastService.show({
      message: message,
      type: 'info',
      autoClose: true,
      autoCloseDelay: 10000
    });
  }

  async openModalForErrorMessage() {
    const instance = await this.modalService.open({
      content: this.customModalForErrorMessage
    })
    instance.htmlElement.addEventListener('keydown',(keyboardEvent: KeyboardEvent) => {});
  }

  async successToast(message: string) {
    this.toastService.setPosition('top-right');
    this.toastService.show({
      message: message,
      type: 'success',
      autoClose: false,
      autoCloseDelay: 10000
    });
  }

  public fileTypeChanged(event: any): void {
    this.fileTypeSelected = true;
    if(this.editStateService.getEditedState()){
      this.openModalForUnsavedChanges(event)
    }else{
      this.enableEditOption = false
      this.fileType = event
      this.file = CrmtFileFactory.create(this.fileType)

      if(this.fileType == "attached-systems-parameters" || this.fileType == "train-crew-parameters" 
        || this.fileType == "software-package" || this.fileType == "response-action-database" || this.fileType== "period-timer-parameters") {
        this.newButtonDisable = true
      } else {
        this.newButtonDisable = false
      }

      if(this.fileType == "software-package" || this.fileType == "response-action-database" || this.fileType== "period-timer-parameters") {
        this.loadButtonDisable = true
      } else {
        this.loadButtonDisable = false
      }
      this.expectedFileConfig = this.getConfig(event)
      this.validExtensions = this.expectedFileConfig.fileTypes.join(", ")
      this.localOperationsDisabled = false
      this.saveAndUploadDisabled = true
      this.updateFileList()
    }
  }

  private getConfig(type: string) {

    let config: LocalConfig = {
      type,
      fileCount: 1,
      fileTypes: [""]
    }

    switch(type) {

      case "standard-phonebook":
        config.fileTypes  = [".spb", ".db"]; 
        break;
      
      case "extended-phonebook": 
        config.fileTypes = [".epb", ".db"]; 
        break;

      case "sms-database": 
        config.fileTypes = [".sms", ".db"]; 
        break;

      case "uus-database": 
        config.fileTypes = [".uus", ".db"];
        break;

      case "software-package": 
        config.fileCount = 2;
        config.fileTypes = [".ini", ".tar.gz"]; 
        break;
      
      default:
        config.fileTypes = [".db"];
        break;
    }

    return config;
  }
  
  private validateFileExtension(filename: string, extensions: string[]): boolean {
    let valid = false
    extensions.forEach(ext => {
      valid ||= filename.endsWith(ext)
    })
    return valid
  }

  private validateFileSelection(filename: string, fileSize:number, config: LocalConfig) {

    if(fileSize > this.allowedFileSize){
      this.errorMessage = `Maximum allowed file size is 1 MB`;
      this.openModalForErrorMessage();
      return false;
    }

    if(!this.validateFileExtension(filename,config.fileTypes)){
      this.errorMessage = `Invalid file extension - expected ${config.fileTypes}`;
      this.openModalForErrorMessage();
      return false;
    }
    return true;
  }


  public selectedFileType(event: any){
    this.originalSelectedValue = event
  }


  public deviceTypeChanged(event: any): void {
    this.enableEditOption = false
    this.deviceType = event
    this.cloudOperationsDisabled = false
    this.saveAndUploadDisabled = true
    this.updateFileList()
  }


  private updateFileList() {

    this.fileList = []
    if (this.fileType && this.deviceType) {
      this._apiService.getFileList(
        this.fileType,
        this.deviceType).subscribe((data: FileListItem[]) => {
          this.fileList = data
        })
    }
  }

  async openModal() {
    const instance = await this.modalService.open({
      size: '480',
      content: this.customModalRef,
      data: 'Some data',
    });

    instance.onClose.on((a) => {
      this.enableEditOption = true
      this.isLoading = true
      const filename = `uploads/${this.fileType}/${this.deviceType}/${this.fileForDownload}`

      if (this.fileType == 'software-package') {
        this.showToastMessageForSoftwarePackageDownloading();
        this.enableEditOption = false
        this.isLoading = false
        this._apiService.getSoftwarePackageFileList(filename).pipe(
          switchMap((data: FileListItem[]) => this._apiService.downloadSoftwarePackage(filename, data))
        ).subscribe({
          error: (e) => {
            if (e.status == 0) { // This is the 0 = unknown Error
              this.errorMessage = "Unable to process your request. Please try again later or contact the administrator."
              this.openModalForErrorMessage()
            } else {
              this.errorMessage = `Error occurred while downloading file. Please contact administrator.`
              this.openModalForErrorMessage()
            }
          }
        })
      } else {
        this._apiService.download(filename, this.fileType).subscribe({
          next: (data) => {
            this.isLoading = false
            this.isError = false
            this.file = data
            if(this.fileType == "response-action-database" || this.fileType== "period-timer-parameters"){
              this.saveAndUploadDisabled = true;
            }
            else{
              this.saveAndUploadDisabled = false
            }
          },
          error: (e) => {
            if (e.status == 0) {// This is the 0 = unknown Error
              this.errorMessage = "Unable to process your request. Please try again later or contact the administrator."
              this.openModalForErrorMessage()
            } else {
              this.errorMessage = `Error occurred while downloading file. Please contact administrator.`
              this.openModalForErrorMessage()
            }
            this.isError = true
            this.isLoading = false
          }
        })
      }
    });

    instance.htmlElement.addEventListener('keydown',(keyboardEvent: KeyboardEvent) => {});
  }

  async showToastMessageForSoftwarePackageDownloading() {
    this.toastService.setPosition('top-right');
    this.toastService.show({
      message: 'File Downloading...',
      type: "info",
      autoCloseDelay: 10000
    });
  }

  public selection(event: string | undefined) {
    if(event != null) {
      this.downloadOkOptionDisabled = false
    }
    this.fileForDownload = event
  }

  public selectedForDeletion(event: string) {
    if (!this.filesForDelete.includes(event)) {
      this.filesForDelete.push(event);
    } else {
      this.filesForDelete = this.filesForDelete.filter(item => item !== event);
    }
    this.deleteOptionMode()
  }

  public deleteOptionMode() {
    if (this.filesForDelete.length >= 1) {
      this.deleteOptionDisabled = false
    } else {
      this.deleteOptionDisabled = true
    }
  }

  async openModalForDeleteWindow() {
    const instance = await this.modalService.open({
      size: '480',
      content: this.customModalRefForDeleteWindow,
      data: 'Some data',
    });

    instance.onClose.on((a) => {
      const deleteFileArray: DeleteFileBody[] = this.filesForDelete.map((fileName) => ({
        fileName: `uploads/${this.fileType}/${this.deviceType}/${fileName}`,
        comment: "Deleting Job from CRMT UI",
      }));
      console.info("Delete Job Array", JSON.stringify(deleteFileArray));

      this._apiService.deleteFileFromAWS(deleteFileArray).subscribe({
        next: (response) => {
          if(response.message.length != 0) {
            this.showToastMessage();
          }
          console.debug('Response:', response);
        },
        error: (error) => {
          console.error('Error occurred:', error);
          this.errorMessage = error.error.message || ""
          this.openModalForErrorMessage()
        }
      });
      this.filesForDelete = []
    });

    instance.htmlElement.addEventListener('keydown',(keyboardEvent: KeyboardEvent) => {});
  }

  async showToastMessage() {
    this.toastService.setPosition('top-right');
    this.toastService.show({
      message: 'Files Deletion Successful.',
      type: "success",
      autoCloseDelay: 10000
    });
  }
  async openModalForUnsavedChanges(event?: any) {
    const instance = await this.modalService.open({
      content: this.customModalForUnsavedChangesRef,
      data: '',
    })

    instance.onClose.on((a) => {//on click of Ok button on popup
      const selectedItem = this.elementRef.nativeElement.querySelector('ix-select');
      this.originalSelectedValue = selectedItem.value
      this.editStateService.setEditedState(false)
      this.fileTypeChanged(event)
    });

    instance.onDismiss.on((arg) => { // when cancel is clicked on popup
      const selectedItem = this.elementRef.nativeElement.querySelector('ix-select')
      selectedItem.value = this.originalSelectedValue
    })

    instance.htmlElement.addEventListener('keydown',(keyboardEvent: KeyboardEvent) => {});

  }

  ngOnDestroy() {
    this.editStateService.setEditedState(false);
  }


  async showLoadSuccessToastMessage() {
    this.toastService.setPosition('top-right');
    this.toastService.show({
      message: 'Files Loaded Successfully.',
      type: "success",
      autoCloseDelay: 10000
    });

  }

}
